import { EvaluationType } from '../models/Evaluation'
import {
  BecameBuddyNotification,
  BecameCustomLeadNotification,
  BecameLeadNotification,
  CollectionNoteNotification,
  EvaluationReceivedNotification,
  EvaluationRevokedNotification,
  FeedbackReceivedExternalNotification,
  FeedbackReceivedNotification,
  FeedbackRevokedNotification,
  GoalCommentNotification,
  INotificationPure,
  JournalAccessLostNotification,
  JournalInactivityNotification,
  JournalNewEntryNotification,
  NewMemberInTeamNotification,
  NotificationType,
  OnboardingCompleteNotification,
  OnboardingProgressNotification,
  OnboardingRunningLateNotification,
  OnboardingStartedNotification,
  StoppedBeingCustomLeadNotification,
  EvaluationResultsAvailableNotification,
  GoalsReviewedNotification,
  GoalsCarriedOverNotification,
  JournalAccessGrantedNotification,
  TeamGoalsPublishedNotification,
  TeamSelfEvaluationSubmittedNotification,
  TeamFeedbackExchangedNotification,
  CompanyMemberJoinedNotification,
  CompanyMemberArchivedNotification,
  CompanyTermStartedNotification,
  CompanyEvaluationsStartedNotification,
  CompanyEvaluationsClosedNotification,
  CompanySurveyCompletedNotification,
  TeammateJoinedNotification,
  TeammateLeftNotification,
  JobUpdatedNotification,
} from '../models/Notification'

export interface INotificationContent {
  titleKey: string
  params: Record<string, any>
  url: string
}

/**
 * Maps a notification to its display content: the i18n key (from the shared
 * `emails.summary.*` copy), the interpolation params (color-free — each caller
 * injects its own `color`), and a relative deep-link url ('' when there's none).
 *
 * Single source of truth for both the weekly summary email and the in-app
 * notifications popover. The email prepends its BASE_URL to `url` and passes a
 * `color` param; the popover renders `url` as a route and colors via the theme.
 */
export const getNotificationContent = (
  notification: INotificationPure
): INotificationContent => {
  const onboardingUrl = notification.context?.onboardingId
    ? `/company/onboarding/journey/${notification.context.onboardingId}`
    : ''
  // The row is about a specific person, so link to them rather than to a list.
  const profileUrl = (tab: string) =>
    notification.initiator?.memberId
      ? `/company/people/profile/${notification.initiator.memberId}/${tab}`
      : ''
  const memberProfileUrl = profileUrl('collections')

  // Someone else's journal lives on their profile; only your own is under /profile.
  const journalUrl = (ownerId?: string) =>
    ownerId && '' + ownerId !== '' + notification.memberId
      ? `/company/people/profile/${ownerId}/journals`
      : '/profile/journals'

  switch (notification.type) {
    // Feedback Notifications
    case NotificationType.FEEDBACK_RECEIVED: {
      const nf = notification as FeedbackReceivedNotification
      return {
        titleKey: 'emails.summary.feedbackReceived',
        params: { name: nf.initiator.name },
        url: '/profile/feedback',
      }
    }

    case NotificationType.FEEDBACK_RECEIVED_EXTERNAL: {
      const nf = notification as FeedbackReceivedExternalNotification
      return {
        titleKey: 'emails.summary.feedbackReceivedExternal',
        params: {
          name: nf.initiator.name,
          companyName: nf.context.companyName,
        },
        url: '/profile/feedback',
      }
    }

    case NotificationType.FEEDBACK_REVOKED: {
      const nf = notification as FeedbackRevokedNotification
      return {
        titleKey: 'emails.summary.feedbackRevoked',
        params: { name: nf.initiator.name },
        url: '',
      }
    }

    // Goals Notifications
    case NotificationType.GOAL_COMMENT: {
      const nf = notification as GoalCommentNotification
      return {
        titleKey: 'emails.summary.goalComment',
        params: { name: nf.initiator.name },
        url: '/profile/goals',
      }
    }

    case NotificationType.GOALS_REMINDER:
      return {
        titleKey: 'emails.summary.goalsReminder',
        params: {},
        url: '/profile/goals',
      }

    case NotificationType.GOALS_UNPUBLISHED_TEAM_LEAD:
      return {
        titleKey: 'emails.summary.goalsUnpublishedTeamLead',
        params: {},
        url: '/team',
      }

    case NotificationType.GOALS_UNPUBLISHED_CUSTOM_LEAD:
      return {
        titleKey: 'emails.summary.goalsUnpublishedCustomLead',
        params: {},
        url: '/team',
      }

    // Journal Notifications
    case NotificationType.JOURNAL_INACTIVITY: {
      const nf = notification as JournalInactivityNotification
      return {
        titleKey: 'emails.summary.journalInactivity',
        params: { journalTitle: nf.context.journalTitle },
        url: '/profile/journals',
      }
    }

    case NotificationType.JOURNAL_ACCESS_LOST: {
      const nf = notification as JournalAccessLostNotification
      return {
        titleKey: 'emails.summary.journalAccessLost',
        params: { name: nf.initiator.name },
        url: '',
      }
    }

    case NotificationType.JOURNAL_NEW_ENTRY: {
      const nf = notification as JournalNewEntryNotification
      return {
        titleKey: 'emails.summary.journalNewEntry',
        params: {
          name: nf.initiator.name,
          journalTitle: nf.context.journalTitle,
        },
        url: journalUrl(nf.context.journalOwnerId),
      }
    }

    // Collection Notifications
    case NotificationType.COLLECTION_NEW:
      return {
        titleKey: 'emails.summary.collectionNew',
        params: {},
        url: '/profile/collections',
      }

    case NotificationType.COLLECTION_NOTE: {
      const nf = notification as CollectionNoteNotification
      return {
        titleKey: 'emails.summary.collectionNote',
        params: { name: nf.initiator.name },
        url: '/profile/collections',
      }
    }

    // Evaluation Notifications
    case NotificationType.EVALUATION_REVOKED: {
      const nf = notification as EvaluationRevokedNotification
      return nf.initiator?.name
        ? {
            titleKey: 'emails.summary.evaluationRevokedWithName',
            params: { name: nf.initiator.name },
            url: '',
          }
        : {
            titleKey: 'emails.summary.evaluationRevokedGeneric',
            params: {},
            url: '',
          }
    }

    case NotificationType.EVALUATION_RECEIVED: {
      const nf = notification as EvaluationReceivedNotification
      // The part evaluated has to live in the key, not in a parameter: it is an
      // English enum value, so interpolating it put "goal beoordeling" in the
      // Dutch mail. Older rows may predate the context, hence the generic key.
      const PART_KEY: Record<string, string> = {
        [EvaluationType.GOAL]: 'emails.summary.evaluationReceivedGoal',
        [EvaluationType.SKILL]: 'emails.summary.evaluationReceivedSkill',
        [EvaluationType.CULTURE]: 'emails.summary.evaluationReceivedCulture',
      }
      return {
        titleKey:
          PART_KEY[nf.context.evaluationType] ||
          'emails.summary.evaluationReceived',
        params: { name: nf.initiator.name },
        url: '/profile/collections',
      }
    }

    // User State Notifications
    case NotificationType.BECAME_BUDDY: {
      const nf = notification as BecameBuddyNotification
      return {
        titleKey: 'emails.summary.becameBuddy',
        params: { name: nf.initiator.name },
        url: memberProfileUrl,
      }
    }

    case NotificationType.BECAME_LEAD: {
      const nf = notification as BecameLeadNotification
      return {
        titleKey: 'emails.summary.becameLead',
        params: { teamName: nf.context.teamName },
        url: '/team',
      }
    }

    case NotificationType.STOPPED_BEING_CUSTOM_LEAD: {
      const nf = notification as StoppedBeingCustomLeadNotification
      return {
        titleKey: 'emails.summary.stoppedBeingCustomLead',
        params: { name: nf.initiator.name },
        url: '',
      }
    }

    case NotificationType.BECAME_CUSTOM_LEAD: {
      const nf = notification as BecameCustomLeadNotification
      return {
        titleKey: 'emails.summary.becameCustomLead',
        params: { name: nf.initiator.name },
        url: memberProfileUrl,
      }
    }

    case NotificationType.NEW_MEMBER_IN_TEAM: {
      const nf = notification as NewMemberInTeamNotification
      return {
        titleKey: 'emails.summary.newMemberInTeam',
        params: { name: nf.initiator.name },
        url: '/team',
      }
    }

    // Onboarding Notifications
    case NotificationType.ONBOARDING_STARTED: {
      const nf = notification as OnboardingStartedNotification
      return {
        titleKey: 'emails.summary.onboardingStarted',
        params: { name: nf.initiator.name },
        url: onboardingUrl,
      }
    }

    case NotificationType.ONBOARDING_COMPLETE: {
      const nf = notification as OnboardingCompleteNotification
      return {
        titleKey: 'emails.summary.onboardingComplete',
        params: { name: nf.initiator.name },
        url: onboardingUrl,
      }
    }

    case NotificationType.ONBOARDING_PROGRESS: {
      const nf = notification as OnboardingProgressNotification
      const percent = Math.round(
        (nf.context.completedTasks / nf.context.totalTasks) * 100
      )
      const isSelf = nf.initiator.memberId + '' === notification.memberId + ''
      return isSelf
        ? {
            titleKey: 'emails.summary.onboardingProgressSelf',
            params: {
              completedTasks: nf.context.completedTasks,
              percent,
              weeksLeft: nf.context.weeksLeft,
            },
            url: onboardingUrl,
          }
        : {
            titleKey: 'emails.summary.onboardingProgress',
            params: {
              name: nf.initiator.name,
              completedTasks: nf.context.completedTasks,
              percent,
              weeksLeft: nf.context.weeksLeft,
            },
            url: onboardingUrl,
          }
    }

    case NotificationType.ONBOARDING_RUNNING_LATE: {
      const nf = notification as OnboardingRunningLateNotification
      return nf.initiator.memberId === notification.memberId
        ? {
            titleKey: 'emails.summary.onboardingRunningLateSelf',
            params: {},
            url: onboardingUrl,
          }
        : {
            titleKey: 'emails.summary.onboardingRunningLate',
            params: { name: nf.initiator.name },
            url: onboardingUrl,
          }
    }

    // ------------------ Personal development ------------------
    case NotificationType.EVALUATION_RESULTS_AVAILABLE: {
      const nf = notification as EvaluationResultsAvailableNotification
      return {
        titleKey: 'emails.summary.evaluationResultsAvailable',
        params: { termNumber: nf.context.termNumber },
        url: '/profile/collections',
      }
    }

    case NotificationType.GOALS_REVIEWED: {
      const nf = notification as GoalsReviewedNotification
      return {
        titleKey: 'emails.summary.goalsReviewed',
        params: { name: nf.initiator.name },
        url: '/profile/goals',
      }
    }

    case NotificationType.GOALS_CARRIED_OVER: {
      const nf = notification as GoalsCarriedOverNotification
      return {
        titleKey: 'emails.summary.goalsCarriedOver',
        params: { termNumber: nf.context.termNumber },
        url: '/profile/goals',
      }
    }

    case NotificationType.JOURNAL_ACCESS_GRANTED: {
      const nf = notification as JournalAccessGrantedNotification
      return {
        titleKey: 'emails.summary.journalAccessGranted',
        params: {
          name: nf.initiator.name,
          journalTitle: nf.context.journalTitle,
        },
        // The initiator of an access change is the journal owner.
        url: journalUrl(nf.initiator.memberId),
      }
    }

    case NotificationType.BECAME_ADMIN:
      return {
        titleKey: 'emails.summary.becameAdmin',
        params: {},
        url: '/company',
      }

    case NotificationType.STOPPED_BEING_ADMIN:
      return {
        titleKey: 'emails.summary.stoppedBeingAdmin',
        params: {},
        url: '',
      }

    case NotificationType.GAINED_VIEW_ALL:
      return {
        titleKey: 'emails.summary.gainedViewAll',
        params: {},
        url: '/company',
      }

    case NotificationType.LOST_VIEW_ALL:
      return {
        titleKey: 'emails.summary.lostViewAll',
        params: {},
        url: '',
      }

    // ------------------ Lead insights ------------------
    case NotificationType.TEAM_GOALS_PUBLISHED: {
      const nf = notification as TeamGoalsPublishedNotification
      return {
        titleKey: 'emails.summary.teamGoalsPublished',
        params: { name: nf.initiator.name },
        url: profileUrl('goals'),
      }
    }

    case NotificationType.TEAM_SELF_EVALUATION_SUBMITTED: {
      const nf = notification as TeamSelfEvaluationSubmittedNotification
      return {
        titleKey: 'emails.summary.teamSelfEvaluationSubmitted',
        params: { name: nf.initiator.name },
        url: profileUrl('collections'),
      }
    }

    case NotificationType.TEAM_FEEDBACK_EXCHANGED: {
      const nf = notification as TeamFeedbackExchangedNotification
      return {
        titleKey: 'emails.summary.teamFeedbackExchanged',
        params: { name: nf.initiator.name },
        url: profileUrl('feedback'),
      }
    }

    // ------------------ Company (admins) ------------------
    case NotificationType.COMPANY_MEMBER_JOINED: {
      const nf = notification as CompanyMemberJoinedNotification
      return {
        titleKey: 'emails.summary.companyMemberJoined',
        params: { name: nf.initiator.name },
        url: memberProfileUrl,
      }
    }

    case NotificationType.COMPANY_MEMBER_ARCHIVED: {
      const nf = notification as CompanyMemberArchivedNotification
      return {
        titleKey: 'emails.summary.companyMemberArchived',
        params: { name: nf.initiator.name },
        url: '/company/people',
      }
    }

    case NotificationType.COMPANY_TERM_STARTED: {
      const nf = notification as CompanyTermStartedNotification
      return {
        titleKey: 'emails.summary.companyTermStarted',
        params: { termNumber: nf.context.termNumber },
        url: '/company/rituals',
      }
    }

    case NotificationType.COMPANY_EVALUATIONS_STARTED: {
      const nf = notification as CompanyEvaluationsStartedNotification
      return {
        titleKey: 'emails.summary.companyEvaluationsStarted',
        params: { termNumber: nf.context.termNumber },
        url: '/company/rituals/evaluations',
      }
    }

    case NotificationType.COMPANY_EVALUATIONS_CLOSED: {
      const nf = notification as CompanyEvaluationsClosedNotification
      return {
        titleKey: 'emails.summary.companyEvaluationsClosed',
        params: { termNumber: nf.context.termNumber },
        url: '/company/rituals/evaluations',
      }
    }

    case NotificationType.COMPANY_SURVEY_COMPLETED: {
      const nf = notification as CompanySurveyCompletedNotification
      return {
        titleKey: 'emails.summary.companySurveyCompleted',
        params: { surveyName: nf.context.surveyName },
        url: `/company/engagement/distribution/${nf.context.distributionId}`,
      }
    }

    case NotificationType.TEAMMATE_JOINED: {
      const nf = notification as TeammateJoinedNotification
      return {
        titleKey: 'emails.summary.teammateJoined',
        params: { name: nf.initiator.name },
        url: `/profile/${nf.initiator.memberId}`,
      }
    }

    case NotificationType.TEAMMATE_LEFT: {
      const nf = notification as TeammateLeftNotification
      return {
        titleKey: 'emails.summary.teammateLeft',
        params: { name: nf.initiator.name },
        url: `/profile/${nf.initiator.memberId}`,
      }
    }

    case NotificationType.JOB_UPDATED: {
      const nf = notification as JobUpdatedNotification
      // Rows written before the schema declared jobTitle have no context at all.
      const jobTitle = nf.context?.jobTitle
      return {
        titleKey: jobTitle
          ? 'emails.summary.jobUpdated'
          : 'notifications.titles.jobUpdated',
        params: jobTitle
          ? { name: nf.initiator.name, jobTitle }
          : { name: nf.initiator.name },
        url: '/dashboard',
      }
    }

    default:
      return { titleKey: '', params: {}, url: '' }
  }
}
